import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { IPGeolocation, IPGeolocationSummary, toGeolocationSummary } from '../types/ip-geolocation.types';

@Injectable({ providedIn: 'root' })
export class IPGeolocationService {
    // Alternative: ip-api.com - Free, supports CORS, 45 requests/minute
    // More reliable than ipwho.is which recently disabled CORS on free plan
    // Proxied through /ipapi/ to avoid mixed content error on HTTPS sites
    private readonly API_URL = '/ipapi/json';

    // Cache for geolocation results (key: IP, value: Observable)
    private cache = new Map<string, Observable<IPGeolocationSummary | null>>();
    private cacheTimeout = 60 * 60 * 1000; // 1 hour (geolocation data changes rarely)

    constructor(private http: HttpClient) { }

    /**
     * Get geolocation information for an IP address using ip-api.com
     * Free tier: 45 requests per minute, no API key required, CORS enabled
     * @param ip IP address to lookup
     * @returns Observable with geolocation summary or null if error
     */
    lookupIP(ip: string): Observable<IPGeolocationSummary | null> {
        console.log('🌍 IPGeolocationService: Looking up geolocation for:', ip);

        // Check cache first
        if (this.cache.has(ip)) {
            console.log('📦 IPGeolocationService: Returning cached result for:', ip);
            return this.cache.get(ip)!;
        }

        const url = `${this.API_URL}/${ip}`;

        const request$ = this.http.get<any>(url).pipe(
            map(response => this.mapIpApiToGeolocation(response)),
            tap(response => {
                console.log('✅ IPGeolocationService: Received geolocation data:', response);
            }),
            map(response => toGeolocationSummary(response)),
            tap(summary => {
                if (summary) {
                    console.log('✅ IPGeolocationService: Geolocation summary:', summary);
                } else {
                    console.log('⚠️ IPGeolocationService: Failed to get geolocation');
                }
            }),
            catchError(error => {
                console.error('❌ IPGeolocationService: Error looking up geolocation:', error);
                // Return null on error
                return of(null);
            }),
            shareReplay(1) // Share the result among multiple subscribers
        );

        // Store in cache
        this.cache.set(ip, request$);

        // Clear from cache after timeout
        setTimeout(() => {
            this.cache.delete(ip);
            console.log('🗑️ IPGeolocationService: Cache expired for:', ip);
        }, this.cacheTimeout);

        return request$;
    }

    /**
     * Clear all cached results
     */
    clearCache(): void {
        this.cache.clear();
        console.log('🗑️ IPGeolocationService: Cache cleared');
    }

    /**
     * Map ip-api.com response to IPGeolocation format
     * ip-api.com fields: status, country, countryCode, region, regionName, city, 
     * zip, lat, lon, timezone, isp, org, as, query
     */
    private mapIpApiToGeolocation(response: any): IPGeolocation {
        // ip-api.com returns status: 'success' or 'fail'
        const success = response.status === 'success';

        return {
            ip: response.query || '',
            success: success,
            type: 'IPv4',
            continent: '', // ip-api doesn't provide continent
            continent_code: '',
            country: response.country || '',
            country_code: response.countryCode || '',
            region: response.regionName || '',
            region_code: response.region || '',
            city: response.city || '',
            latitude: response.lat || 0,
            longitude: response.lon || 0,
            is_eu: false, // ip-api doesn't provide EU flag
            postal: response.zip || '',
            calling_code: '',
            capital: '',
            borders: '',
            connection: {
                asn: 0,
                org: response.org || '',
                isp: response.isp || '',
                domain: ''
            },
            timezone: {
                id: response.timezone || '',
                abbr: '',
                is_dst: false,
                offset: 0,
                utc: '',
                current_time: ''
            },
            message: response.message || ''
        };
    }
}
